/**
 * Thin API fetch wrapper.
 *
 * In dev: Vite proxy forwards `/api` → `localhost:8080`.
 * In prod: CloudFront forwards `/api` → ALB origin (see vite.config.ts comment).
 *
 * VITE_FUND_IMAGES_CF_DOMAIN is used to construct absolute CloudFront image
 * URLs from the relative S3 keys stored in DDB.
 */

const CF_DOMAIN = import.meta.env.VITE_FUND_IMAGES_CF_DOMAIN ?? "";

/**
 * Auth issuer routes (gate-exempt) live under `/api/auth/*`. Same-origin in
 * every deployed env (CloudFront routes `/api/*` to the ALB); overridable via
 * VITE_AUTH_BASE.
 */
const AUTH_BASE = import.meta.env.VITE_AUTH_BASE ?? "/api/auth";

/**
 * Send the user to the issuer login, preserving where they were via `?next=`.
 * Throws to halt the in-flight promise chain while the browser navigates away.
 */
function redirectToLogin(): never {
  const next = window.location.pathname + window.location.search;
  window.location.assign(`${AUTH_BASE}/login?next=${encodeURIComponent(next)}`);
  throw new Error("redirecting to sign in");
}

/**
 * Build a CloudFront URL for a fund image key stored in DDB.
 * Returns `undefined` when there's no key or the CloudFront domain isn't
 * configured (e.g. local dev without an env override) so callers can render a
 * graceful fallback instead of an empty `<img src="">` (which makes the browser
 * refetch the page URL).
 */
export function fundImageUrl(key: string | undefined): string | undefined {
  if (!key) return undefined;
  if (!CF_DOMAIN) return undefined;
  return `https://${CF_DOMAIN}/${key}`;
}

/**
 * Fetch a JSON resource from the API. Throws on non-2xx responses so TanStack
 * Query can surface them as errors without extra boilerplate per call-site.
 */
export async function apiFetch<T>(path: string): Promise<T> {
  // credentials:"include" so the shared `__Secure-cf_at` session cookie rides
  // along on the gated dev/staging hosts. Prod web is open, so it's a harmless
  // no-op there.
  const res = await fetch(path, { credentials: "include" });
  // On dev/staging the session gate is ON: a 401 means the cookie is missing or
  // expired → bounce to the issuer login. (Prod web is open, so a 401 shouldn't
  // occur there in the first place.)
  if (res.status === 401) redirectToLogin();
  if (!res.ok) {
    throw new Error(`API ${res.status} for ${path}`);
  }
  return res.json() as Promise<T>;
}
